import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getArticleDiscountById,
  putArticleDiscount,
} from "../../app/services/articleDiscountService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import Form from "react-bootstrap/Form";
import { Alert, Breadcrumb, Button, Col, Row } from "react-bootstrap";
import {
  ArticleDiscount,
  defaultArticleDiscount,
} from "../../models/responses/articleDiscount";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

interface Props {}

const ArticleDiscountEdit: FC<Props> = () => {
  const { articleDiscountId } = useParams<{
    articleDiscountId: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [displayError, setDisplayError] = useState(false);
  const isWaitingArticleDiscountPutResponse = useSelector(
    (state: RootState) =>
      state.articleDiscount.isWaitingArticleDiscountPutResponse,
  );
  const articleDiscount = useSelector(
    (state: RootState) => state.articleDiscount.articleDiscount,
  );
  const [articleDiscountData, setArticleDiscountData] =
    useState<ArticleDiscount>(defaultArticleDiscount);

  useEffect(() => {
    dispatch(
      getArticleDiscountById(instance, account, articleDiscountId),
    );
  }, [account, accounts, dispatch, instance, articleDiscountId]);
  useEffect(() => {
    if (articleDiscount) {
      setArticleDiscountData({
        id: articleDiscount.id,
        name: articleDiscount.name,
        description: articleDiscount.description,
        externalId: articleDiscount.externalId,
        discountPercentage: articleDiscount.discountPercentage,
        forcedSalesPrice: articleDiscount.forcedSalesPrice,
        createdAt: articleDiscount.createdAt,
        updatedAt: articleDiscount.updatedAt,
      });
    }
  }, [articleDiscount]);

  const handleUpdateArticleDiscount = () => {
    setDisplayError(false);
    dispatch(
      putArticleDiscount(
        instance,
        account,
        articleDiscountData,
        onSuccess,
      ),
    );
  };

  const onSuccess = (articleDiscountId: string | null) => {
    if (articleDiscountId === null) {
      setDisplayError(true);
      return;
    }

    setTimeout(() => {
      navigate(`../../${articleDiscountId}`, { relative: "path" });
    }, 500);
  };

  return (
    <div>
      <Breadcrumb>
        <Breadcrumb.Item href="/article-discounts">
          Artikelrabatter
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Uppdatera artikelrabatt</Breadcrumb.Item>
      </Breadcrumb>
      <hr />
      <Form className="w-3">
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Artikel (ADID)</Form.Label>
              <Form.Control
                type="text"
                value={articleDiscountData.externalId}
                onInput={(e) =>
                  setArticleDiscountData({
                    ...articleDiscountData,
                    externalId: e.currentTarget.value,
                  })
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Namn *</Form.Label>
              <Form.Control
                type="text"
                value={articleDiscountData.name}
                onInput={(e) =>
                  setArticleDiscountData({
                    ...articleDiscountData,
                    name: e.currentTarget.value,
                  })
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Beskrivning *</Form.Label>
              <Form.Control
                type="text"
                value={articleDiscountData.description}
                onInput={(e) =>
                  setArticleDiscountData({
                    ...articleDiscountData,
                    description: e.currentTarget.value,
                  })
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Rabatt %</Form.Label>
              <Form.Control
                type="number"
                value={articleDiscountData.discountPercentage}
                onInput={(e) =>
                  setArticleDiscountData({
                    ...articleDiscountData,
                    discountPercentage: Number(e.currentTarget.value),
                  })
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Fastpris</Form.Label>
              <Form.Control
                type="text"
                value={articleDiscountData.forcedSalesPrice ?? ""}
                onInput={(e) =>
                  setArticleDiscountData({
                    ...articleDiscountData,
                    forcedSalesPrice: e.currentTarget.value,
                  })
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="text-end">
          <Button
            variant="primary"
            className="mb-3"
            onClick={handleUpdateArticleDiscount}
            disabled={
              !(
                articleDiscountData.name !== undefined &&
                articleDiscountData.name.length > 0 &&
                articleDiscountData.description !== undefined &&
                articleDiscountData.description.length > 0 &&
                articleDiscountData.discountPercentage !== undefined &&
                articleDiscountData.discountPercentage >= 0 &&
                !isWaitingArticleDiscountPutResponse
              )
            }
          >
            Uppdatera
          </Button>
        </div>

        <Alert key="alert" variant="danger" show={displayError}>
          <p>Något gick fel, kontrollera uppgifterna och försök igen.</p>
        </Alert>
      </Form>
    </div>
  );
};

export default ArticleDiscountEdit;
